import BaseStore from "../../common/BaseStore";
import i18n from "i18n";
import { observable, action, computed } from "mobx";
import { WBSService } from "modules/WBS/WBSService";
import { WBSStore } from "modules/WBS/WBSStore";
import { WBSProjectData as WBSProjectDataModel } from "modules/WBS/models/WBSProjectData";
import { evaluate } from "mathjs";
import { IBdi, IWBSCalculateRules } from "common/interfaces";
import { NumberToString } from "utils/MathUtils";

class Store extends BaseStore {
  constructor() {
    super("WBSPropertiesStore");
    this.reset();
  }

  @observable wbsCalculate: IWBSCalculateRules = {
    decimalPlaces: 2,
    roundingRule: "round",
  };

  WBSProjectDataModel: WBSProjectDataModel | undefined;
  @observable calculating: boolean = false;

  @action
  reset() {
    this.WBSProjectDataModel = undefined;
  }

  @computed
  get needRecalculate() {
    const { decimalPlaces, roundingRule } = WBSStore.WBS.data;
    return (
      this.wbsCalculate.decimalPlaces !== decimalPlaces ||
      this.wbsCalculate.roundingRule !== roundingRule
    );
  }

  @action
  setCalculating(value: boolean) {
    this.calculating = value;
  }

  setWBSCalculate(name: string, value: any) {
    if (!name) return;
    const data = { ...this.wbsCalculate };
    data[name] = value;
    this.wbsCalculate = data;
  }

  getRules(decimalPlaces?: number, roundingRule?: "trunc" | "round") {
    decimalPlaces = decimalPlaces || this.wbsCalculate.decimalPlaces;
    roundingRule = roundingRule || this.wbsCalculate.roundingRule;
    return { decimalPlaces, roundingRule };
  }

  async getWBSProjectData(wbsId: string, data): Promise<any> {
    if (this.WBSProjectDataModel && this.WBSProjectDataModel.wbsId === wbsId)
      return;

    const model = new WBSProjectDataModel(
      wbsId,
      data.baseMonth !== undefined ? data.baseMonth.toDate() : new Date(),
      data.projectTerm,
      data.landArea,
      data.privateUnits,
      data.builtArea,
      data.equivalentArea,
      data.privateArea,
      data.bdi
    );
    this.WBSProjectDataModel = model;

    return WBSService.subscribeWBSToChangeDecimalRules(wbsId);
  }

  getCurrentWBSBdi(): IBdi {
    if (!this.WBSProjectDataModel) return { show: false, percentual: 0 };
    return this.WBSProjectDataModel.bdi || { show: false, percentual: 0 };
  }

  @action
  async updateWBSProjectData(): Promise<any> {
    if (!this.WBSProjectDataModel) return;
    WBSService.updateWBSProjectData(
      this.WBSProjectDataModel.wbsId,
      this.WBSProjectDataModel
    );
  }

  /**
   * Efetua as validações e cálculos necessários para persistir a fórmula de entrada na linha selecionada
   * @param inputFormula
   */
  applyFormula(inputFormula: any, line: any): any {
    let quantity = 0;
    let valid = false;
    if (inputFormula === undefined) {
      inputFormula = "";
    }

    // Se a fórmula preenchida estiver vazia apenas remove a informação da linha, mantendo a quantidade que já estiver preenchida na linha
    if (inputFormula.length === 0) {
      valid = true;
      // line.data.formula = '';
      WBSStore.handleWBSTableChange(
        line,
        line.index,
        "row",
        "formula",
        "",
        "",
        "",
        "",
        "",
        true
      );
    }
    // Se a fórmula preenchida for inválida deve manter a quantidade da linha zerada
    else if (
      inputFormula.length > 0 &&
      !this.validateFormula(inputFormula, quantity)
    ) {
      valid = false;
      WBSStore.handleWBSTableChange(
        line,
        line.index,
        "row",
        "quantity",
        0,
        "formula",
        inputFormula,
        "",
        "",
        true
      );
    }
    // Se a fórmula preenchida for válida, efetua os tratamentos para efetuar o cálculo de quantidade e persistir as informações da linha
    else {
      line.data.formula = inputFormula;
      const { result, isValidFormula } = this.evaluateFormula(inputFormula);
      quantity = result;
      valid = isValidFormula;
      // Efetua cálculo e atualiza quantidade e fórmula da linha selecionada
      WBSStore.handleWBSTableChange(
        line,
        line.index,
        "row",
        "quantity",
        quantity,
        "formula",
        inputFormula,
        "",
        "",
        true
      );
    }
    return { isValidFormula: valid, quantity };
  }

  public validateFormula(formula: string, quantity: number): boolean {
    formula =
      formula == undefined || formula == "" || formula == " "
        ? quantity + ""
        : formula;
    if (formula == undefined) return false;

    const { isValidFormula } = this.evaluateFormula(formula, true);
    return isValidFormula;
  }

  /**
   * Efetua as substituições na fórmula com as informações globais e faz o cálculo do resultado
   * @param formula Fórmula preenchida pelo usuário: string
   */
  public evaluateFormula(formula: string, isOnEAP: boolean = false) {
    const globalData = this.WBSProjectDataModel;
    let isValidFormula = false;
    let result = 0;

    // Recupera os valores das informações globais definidas no projeto
    let projectTerm = globalData ? NumberToString(globalData.projectTerm) : "0";
    let landArea = globalData ? NumberToString(globalData.landArea) : "0";
    let privateUnits = globalData
      ? NumberToString(globalData.privateUnits)
      : "0";
    let builtArea = globalData ? NumberToString(globalData.builtArea) : "0";
    let equivalentArea = globalData
      ? NumberToString(globalData.equivalentArea)
      : "0";
    let privateArea = globalData ? NumberToString(globalData.privateArea) : "0";

    // Efetua as substituições das variáveis na fórmula de entrada
    formula = formula
      .toLowerCase()
      .replace(/\[área do terreno\]/g, landArea)
      .replace(/\[land area\]/g, landArea)
      .replace(/\[unidades privativas\]/g, privateUnits)
      .replace(/\[private units\]/g, privateUnits)
      .replace(/\[área construída\]/g, builtArea)
      .replace(/\[built area\]/g, builtArea)
      .replace(/\[área equivalente\]/g, equivalentArea)
      .replace(/\[equivalent area\]/g, equivalentArea)
      .replace(/\[área privativa\]/g, privateArea)
      .replace(/\[private area\]/g, privateArea)
      .replace(/\[prazo da obra\]/g, projectTerm)
      .replace(/\[term of work\]/g, projectTerm);

    // Aplica o cálculo na fórmula para recuperar o resultado
    let status = "OK";
    try {
      result =
        i18n.language == "PT"
          ? evaluate(formula.replace(/,/gi, "."))
          : evaluate(formula);

      isValidFormula = true;
    } catch (error) {
      status = "error";
      isValidFormula = !isOnEAP;
      console.log("Invalid formula", formula);
      console.error("Invalid formula", error);
    }

    const isInvalid =
      result < 0 ||
      result === NaN ||
      result === undefined ||
      result == Infinity;

    if (isInvalid) {
      status = result < 0 ? "negative_error" : "error";
      if (isOnEAP) isValidFormula = false;
    }

    // O resultado é manipulado para que seja sempre numérico maior ou igual à zero, mesmo que a fórmula esteja inválida
    result = isInvalid ? 0 : result;
    return { result, isValidFormula, status };
  }

  /**
   * Valida o campo de informação global que teve seu valor alterado para atualizar as linhas do orçamento com fórmulas dependentes
   * @param changedInput Identificação da informação global que foi alterada
   */
  async updateWBSProjectQuantitiesFromFormula(changedInput): Promise<any> {
    let formulaId = "";
    // Recupera a identificação do campo modificado de acordo com o idioma utilizado
    switch (changedInput) {
      case "inputProjectTerm":
        formulaId =
          "[" +
          i18n.project.rightSideMenu.WBSInfo.monthRelease.toUpperCase() +
          "]";
        break;
      case "inputLandArea":
        formulaId =
          "[" + i18n.project.rightSideMenu.WBSInfo.landArea.toUpperCase() + "]";
        break;
      case "inputprivateUnits":
        formulaId =
          "[" +
          i18n.project.rightSideMenu.WBSInfo.privateUnits.toUpperCase() +
          "]";
        break;
      case "inputBuiltArea":
        formulaId =
          "[" +
          i18n.project.rightSideMenu.WBSInfo.builtArea.toUpperCase() +
          "]";
        break;
      case "inputEquivalentArea":
        formulaId =
          "[" +
          i18n.project.rightSideMenu.WBSInfo.equivalentArea.toUpperCase() +
          "]";
        break;
      case "inputPrivateArea":
        formulaId =
          "[" +
          i18n.project.rightSideMenu.WBSInfo.privateArea.toUpperCase() +
          "]";
        break;
    }

    // Filtra as linhas do orçamento que utilizam a informação global do orçamento que foi alterada
    let filter = WBSStore.dataTable.filter(
      ({ data: { formula } }) =>
        formula && formula.toUpperCase().indexOf(formulaId) >= 0
    );

    // Define o índice de verificação da linha no orçamento com o valor default da primeira linha
    let rowIndex = 0;

    // Percorre as linhas filtradas do orçamento
    for (const rowFiltered of filter) {
      let quantity = rowFiltered.data.quantity;
      let formula = rowFiltered.data.formula ? rowFiltered.data.formula : "";
      // Cálcula o resultado da fórmula
      const { result } = this.evaluateFormula(formula);

      // Efetua as atualizações apenas se o resultado da fórmula for diferente da quantidade já definida na linha
      if (quantity !== result) {
        rowIndex = WBSStore.dataTable.indexOf(rowFiltered, rowIndex);
        await WBSStore.handleWBSTableChange(
          rowFiltered,
          rowIndex,
          "row",
          "quantity",
          result,
          "",
          "",
          "",
          "",
          true
        );
      }
    }
  }
}

export const WBSPropertiesStore = new Store();



// WEBPACK FOOTER //
// ./src/modules/WBS/WBSPropertiesStore.tsx